import { teesPropertiesObject } from '../tools/generalProperties';
import { AllTShirtMeasuresMandatory, TeeMeasureLabel } from '../types/databaseTables/idealteemeasures';
import { NewTeeTypesRow } from '../types/databaseTables/newteetypes';
import { TeeMeasuresAveragesRow } from '../types/databaseTables/teemeasuresaverages';
import { FilterAction, GarmentObject, FilterObjectCheckbox, FilterState, FilterTypeLabelsCheckbox, FilterObjectSlider, FilterCheckboxObjectsByCheckboxLabel } from '../types/reducers/filterReducer';
import { logAdminExternal } from '../utils/logging';

export function getConsolidateArrayOfMemoIdArrays(
    arrayOfMemoIdsArrays: number[][]
): number[] {
    // Get shortest array, ignore empty arrays
    const arraysSortedByShortest = arrayOfMemoIdsArrays.filter(x => x.length > 0).sort((a, b) => a.length - b.length);
    const shortestArray = arraysSortedByShortest.shift();
    // If the array of arrays is empty, .shift() will return undefined. If so return an empty array.
    if (shortestArray === undefined) return [];
    return shortestArray.filter(curReturnId =>
        // Filter shortest array against others, only returning true when it's found in all of the others.
        arraysSortedByShortest.filter(curArrayOfIds => curArrayOfIds.includes(curReturnId)).length === arraysSortedByShortest.length
    )
}

export function filterByOtherMemoisedIds(initialFilteredPosts: GarmentObject[], objectOfMemoizedIds: Record<FilterTypeLabelsCheckbox | TeeMeasureLabel, number[]>, thisLabelToOmit: FilterTypeLabelsCheckbox | TeeMeasureLabel | "fit" | "reliability"): GarmentObject[] {
    let returnArray = [...initialFilteredPosts];
    // Create an array of memoizedId arrays.
    // Filter down initialArrayOfPostsIds by the memoised arrays of post IDs
    // Ignore the current label and any arrays that are the same length as the initial array (since they don't have any filters applied). Also ignore any filter than has no values (length === 0), as this could be checkboxes with nothing clicked.
    const arraysOfArraysToIterate = Object.keys(objectOfMemoizedIds).reduce<number[][]>((acc, curFilterLabel) => {
        // If this is the label we're omitting, then skip this filter
        if (curFilterLabel === thisLabelToOmit) return acc;
        const thisArray = objectOfMemoizedIds[curFilterLabel as FilterTypeLabelsCheckbox];
        if (thisArray.length === initialFilteredPosts.length || thisArray.length === 0) return acc;
        acc.push(thisArray)
        return acc;
    }, [] as number[][])

    // Order arrays by smallest first. Then filter initialArrayOfPostIds against each one.
    if (arraysOfArraysToIterate.length > 0) {
        arraysOfArraysToIterate.sort((a, b) => a.length - b.length).forEach(thisArray => {
            returnArray = returnArray.filter(thisGarment => thisArray.includes(thisGarment.teemeasuresaverages_id))
        })
    }
    return returnArray;
}

// Return an array of memoized arrays to be used in the next step, which is to consolidate the arrays into one single array (to filter the array of garment objects by)
export function getArrayOfMemoizedArraysFromObject(
    ObjectOfMemoizedIds: Record<FilterTypeLabelsCheckbox | TeeMeasureLabel | "fit" | "reliability", number[]>,
    initialArrayOfGarmentObjects: GarmentObject[],
): number[][] {

    return Object.keys(ObjectOfMemoizedIds).reduce<number[][]>((acc, cur) => {
        const curArray = ObjectOfMemoizedIds[cur as keyof typeof ObjectOfMemoizedIds]
        // If the memoized array is the same length as the initial array of garments, then omit, since we won't be using it to filter. Also, if it's empty, then no filter checkboxes are checked, so do not filter. I think this is right?
        if (curArray.length === initialArrayOfGarmentObjects.length || curArray.length === 0) return acc;
        acc.push(curArray)
        return acc;
    }, [])
}

// Get the array of Garment Objects after filtering by all the momeized id filters. If the id is missing from any memoised IDs, then exclude it from the returned GarmentObject array.
export function getArrayOfGarmentObjectsFromFilteringAllMemoIds(
    initialArrayOfGarmentObjects: GarmentObject[],
    ObjectOfMemoizedIds: Record<FilterTypeLabelsCheckbox | TeeMeasureLabel | "fit" | "reliability", number[]>,
): GarmentObject[] {
    // Get array of memoized id arrays
    const arrayOfMemoIdsArrays = getArrayOfMemoizedArraysFromObject(ObjectOfMemoizedIds, initialArrayOfGarmentObjects)
    // Get consolidated ids
    const consolidatedArray = getConsolidateArrayOfMemoIdArrays(arrayOfMemoIdsArrays)
    // return filtered array of garment objects
    // If consolidated array is empty, then no filters have been applied
    if (consolidatedArray.length === 0) return initialArrayOfGarmentObjects;
    // Else filter only garments that have filters included
    return initialArrayOfGarmentObjects.filter(curObj => consolidatedArray.includes(curObj.teemeasuresaverages_id))
}

export function getFitAndReliabilityScores(garmentRow: TeeMeasuresAveragesRow, userFitScoreWeightings: AllTShirtMeasuresMandatory, idealGarmentMeasurements: AllTShirtMeasuresMandatory): { fit: number, reliability: number } {
    const totalNumberOfMeasures = Object.keys(userFitScoreWeightings).length;
    // Calculate reliability
    let totalNumberOfValidMeasures = 0;
    let overallFit = 100;
    Object.keys(userFitScoreWeightings).forEach(thisMeasureLabel => {
        // If both user and garment objects have a value > 1, then it is a valid measure
        const thisGarmentValue = garmentRow[thisMeasureLabel as TeeMeasureLabel] || 0
        const thisUserValue = idealGarmentMeasurements[thisMeasureLabel as TeeMeasureLabel] || 0;
        const garmentValueIsValid = thisGarmentValue > 0;
        const userValueIsValid = thisUserValue > 0;
        if (garmentValueIsValid && userValueIsValid) {
            totalNumberOfValidMeasures += 1;
            // Calculate Fit
            const thisWeightingValue = userFitScoreWeightings[thisMeasureLabel as TeeMeasureLabel] || 0;
            // Calculate the percentage difference between user measure and garment measure. Then apply multiplier of weighting
            const thisWeightedFitScore = Math.abs(1 - thisUserValue / thisGarmentValue) * thisWeightingValue * 10;
            overallFit -= thisWeightedFitScore
        }
    })
    return { fit: totalNumberOfValidMeasures > 0 ? Math.max(Math.round(overallFit / 100 * 100) / 100, 0.01) : 0, reliability: (totalNumberOfValidMeasures / totalNumberOfMeasures) || 0 }
}

// Generates garmentObjects from a combination of teetypes, teemeasures, fit and reliability.
export function getInitialArrayOfPosts(teeMeasuresAveragesRows: TeeMeasuresAveragesRow[], newTeeTypesRows: NewTeeTypesRow[], userFitScoreWeightings: AllTShirtMeasuresMandatory, idealGarmentMeasurements: AllTShirtMeasuresMandatory): GarmentObject[] {

    return teeMeasuresAveragesRows.reduce<GarmentObject[]>((acc, thisRow) => {
        // Get the teeTypes row to add to the TeeMeasures row
        const matchingTeeTypesRow = newTeeTypesRows.filter(thisTeeTypeRow => thisTeeTypeRow.newteetypes_id === thisRow.newteetypes_id)[0];
        // Get the fit and reliability score to add to the combined row
        const fitAndReliabilty = getFitAndReliabilityScores(thisRow, userFitScoreWeightings, idealGarmentMeasurements)
        const rowToAdd = { ...thisRow, ...matchingTeeTypesRow, ...fitAndReliabilty };

        // Insert new row object into ordered location of return array (objects to be ordered by fit)
        let spliceIndex = 0;
        // Keeps looping through acc array until either reaches the end of array or finds a place where the above/current fit score is less than the acc[index] fit score.
        while (spliceIndex < acc.length && fitAndReliabilty.fit < acc[spliceIndex].fit) spliceIndex += 1;
        acc.splice(spliceIndex, 0, rowToAdd);
        return acc;
    }, [] as GarmentObject[])
}

export function getInitialArrayOfIds(teeMeasuresAveragesRows: TeeMeasuresAveragesRow[]): number[] {
    return teeMeasuresAveragesRows.map((x) => x.teemeasuresaverages_id)
}

export function getFilterCheckBoxObjectStrings({ label, array }: { label: "description" | "manufacturer", array: NewTeeTypesRow[] }): FilterObjectCheckbox {
    // return an object of checked and disabled booleans with each manufacturer/description
    // First get a sorted array of unique labels
    const uniqueSortedLabels = [...new Set(array.map(thisObj => thisObj[label]))].sort();
    // Then convert this sorted array into the object required:
    return uniqueSortedLabels.reduce<FilterObjectCheckbox>((acc, cur) => {
        const returnObj = { ...acc };
        returnObj[cur] = { checked: false, disabled: false };
        return returnObj;
    }, {} as FilterObjectCheckbox)
}

// 
export function getFilterCheckBoxObject(newTeeTypesArrayState: NewTeeTypesRow[]): FilterCheckboxObjectsByCheckboxLabel {
    return newTeeTypesArrayState
        .reduce((acc, cur) => [...new Set(acc.concat(cur.availablecolors))], [] as string[])
        .sort()
        .reduce((acc, cur) => {
            const returnObj = { ...acc };
            returnObj[cur] = { checked: false, disabled: false };
            return returnObj;
        }, {} as FilterObjectCheckbox)
}

// Updates each filter slider object with users IdealMeasures. Makes each slider object's min and max within 20% of the idealmeasure
export function updateFilterSliderObjects(
    filterSliderObjects: Record<TeeMeasureLabel, FilterObjectSlider>,
    idealGarmentMeasurements: AllTShirtMeasuresMandatory
): Record<TeeMeasureLabel, FilterObjectSlider> {
    return Object.keys(filterSliderObjects).reduce((acc, cur) => {
        const thisMeasureLabel = cur as TeeMeasureLabel;
        const usersIdealMeasure = idealGarmentMeasurements[thisMeasureLabel] || 0;
        const thisFilterObject = {
            min: usersIdealMeasure > 0 ? Math.round(usersIdealMeasure * 0.9) : filterSliderObjects[thisMeasureLabel as TeeMeasureLabel].min,
            max: usersIdealMeasure > 0 ? Math.round(usersIdealMeasure * 1.2) : filterSliderObjects[thisMeasureLabel as TeeMeasureLabel].max,
            disabled: true
        }
        return { ...acc, [cur]: thisFilterObject }
    }, {} as Record<TeeMeasureLabel, FilterObjectSlider>)
}

export function getMemoizedIdArrayFromColors(
    initialArrayOfGarmentObjects: GarmentObject[],
    availableColorsObject: FilterCheckboxObjectsByCheckboxLabel,
): number[] {
    // At end, check if all are false. If so, return full array.
    const arrayOfCheckboxLabels = Object.keys(availableColorsObject)
    if (arrayOfCheckboxLabels.filter(thisCheckboxLabel => availableColorsObject[thisCheckboxLabel].checked).length === 0) return initialArrayOfGarmentObjects.map(x => x.teemeasuresaverages_id);
    // Otherwise return only id numbers where the user has ticked 
    return initialArrayOfGarmentObjects.reduce<number[]>((acc, thisGarmentObj) => {
        const thisGarmentObjIsCheckedInFilter = thisGarmentObj.availablecolors
            .some((thisTagLabel) => availableColorsObject[thisTagLabel].checked);
        if (thisGarmentObjIsCheckedInFilter) {
            return acc.concat(thisGarmentObj.teemeasuresaverages_id)
        }
        return acc;
    }, [])
}

export function getMemoizedIdArrayFromManufacturerOrDescrip(
    initialArrayOfGarmentObjects: GarmentObject[],
    checkboxType: "manufacturer" | "description",
    filterObject: FilterObjectCheckbox
): number[] {
    // if all checkboxes are blank, then return full array
    const arrayOfCheckboxLabelsThatAreChecked = Object.keys(filterObject).filter(thisCheckboxLabel => filterObject[thisCheckboxLabel].checked);
    if (arrayOfCheckboxLabelsThatAreChecked.length === 0) return initialArrayOfGarmentObjects.map(x => x.teemeasuresaverages_id);
    // Else return only id numbers where user has checked the boxes:
    return initialArrayOfGarmentObjects.reduce<number[]>((acc, curGarmObj) => {
        const thisGarmentDescripOrManuf = curGarmObj[checkboxType]
        if (filterObject[thisGarmentDescripOrManuf].checked) {
            return acc.concat(curGarmObj.teemeasuresaverages_id);
        }
        return acc;
    }, [])
}

// Where the user has set a filter value (or if it was default?) then filter the memoisedID array 
export function getMemoizedIdArrayFromFitOrReliability(
    initialArrayOfGarmentObjects: GarmentObject[],
    filterValue: number | null,
    fitOrReliability: "fit" | "reliability"
): number[] {
    // If there is no filter applied then return full array.
    if (filterValue === 0 || filterValue == null) return initialArrayOfGarmentObjects.map(x => x.teemeasuresaverages_id);
    // Otherwise return only id numbers where the user has ticked 
    return initialArrayOfGarmentObjects.reduce<number[]>((acc, thisGarmentObj) => {
        if (filterValue < thisGarmentObj[fitOrReliability] * 100) {
            return acc.concat(thisGarmentObj.teemeasuresaverages_id)
        }
        return acc;
    }, [])
}

export function getMemoizedIdArrayFromSingleSizeSlider(
    initialArrayOfGarmentObjects: GarmentObject[],
    filterLabel: TeeMeasureLabel,
    filterSliderObject: FilterObjectSlider,
): number[] {
    return initialArrayOfGarmentObjects.reduce<number[]>((acc, curGarmentObj) => {
        const garmentValue = curGarmentObj[filterLabel];
        const { min: filterMin, max: filterMax, disabled: filterDisabled } = filterSliderObject;
        // If slider is disabled, or if garment doesn't have a value, or if the value is between the min and max filters, then return acc concatenated with this id.
        if (filterDisabled || garmentValue === 0 || garmentValue === null || (
            garmentValue >= filterMin && garmentValue <= filterMax
        )) {
            return acc.concat(curGarmentObj.teemeasuresaverages_id)
        }
        return acc;
    }, [])
}

export function getMemoizedIdArrayFromSizeSliders(
    initialArrayOfGarmentObjects: GarmentObject[],
    filterSliderObjects: Record<TeeMeasureLabel, FilterObjectSlider>,
): Record<TeeMeasureLabel, number[]> {
    // Get object of memo arrays from the object of filterSliderObjects
    return (Object.keys(filterSliderObjects) as TeeMeasureLabel[]).reduce<Record<TeeMeasureLabel, number[]>>((acc, curLabel) => {
        const createdMemoisedArray = getMemoizedIdArrayFromSingleSizeSlider(
            initialArrayOfGarmentObjects,
            curLabel,
            filterSliderObjects[curLabel]
        )
        return { ...acc, [curLabel]: createdMemoisedArray }
    }, {} as Record<TeeMeasureLabel, number[]>)
}

export function uncheckAllCheckboxes(initialCheckboxObject: FilterObjectCheckbox): FilterObjectCheckbox {
    const returnObj = { ...initialCheckboxObject }
    Object.keys(initialCheckboxObject).forEach(curLabel => {
        returnObj[curLabel].checked = false;
    })
    return returnObj
}

function getUpdatedMemoizedArrays(returnState: FilterState): Record<FilterTypeLabelsCheckbox | TeeMeasureLabel | "fit" | "reliability", number[]> {
    const updatedMeasureSliderMemoizedIdArrays = getMemoizedIdArrayFromSizeSliders(
        returnState.initialArrayOfPosts,
        returnState.filterSliderObjects
    )

    return {
        reliability: getMemoizedIdArrayFromFitOrReliability(
            returnState.initialArrayOfPosts,
            returnState.reliability.min,
            'reliability',
        ),
        fit: getMemoizedIdArrayFromFitOrReliability(
            returnState.initialArrayOfPosts,
            returnState.fit.min,
            'fit',
        ),
        description: getMemoizedIdArrayFromManufacturerOrDescrip(
            returnState.initialArrayOfPosts,
            "description",
            returnState.filterCheckboxObjects.description
        ),
        manufacturer: getMemoizedIdArrayFromManufacturerOrDescrip(
            returnState.initialArrayOfPosts,
            "manufacturer",
            returnState.filterCheckboxObjects.manufacturer
        ),
        availablecolors: getMemoizedIdArrayFromColors(
            returnState.initialArrayOfPosts,
            returnState.filterCheckboxObjects.availablecolors
        ),
        ...updatedMeasureSliderMemoizedIdArrays
    }
}

export function filterReducer(state: FilterState, action: FilterAction) {
    const returnState = { ...state };
    const currentTimestamp = new Date().toUTCString()
    switch (action.type) {
        case 'clear all filters': {
            // clear measure sliders
            Object.keys(returnState.filterSliderObjects).forEach(curMeasureLabel => {
                returnState.filterSliderObjects[curMeasureLabel as TeeMeasureLabel].disabled = true
            })
            // clear checkboxes: manufacturer, description, available colors
            returnState.filterCheckboxObjects.manufacturer = uncheckAllCheckboxes(returnState.filterCheckboxObjects.manufacturer)
            returnState.filterCheckboxObjects.description = uncheckAllCheckboxes(returnState.filterCheckboxObjects.description)
            returnState.filterCheckboxObjects.availablecolors = uncheckAllCheckboxes(returnState.filterCheckboxObjects.availablecolors)
            // clear fit and reliability
            returnState.reliability = { disabled: true, min: 0 };
            returnState.fit = { disabled: true, min: 0 };
            // Update internal posts with latest filter object info;
            returnState.memoizedIdArrays = getUpdatedMemoizedArrays(returnState)
            returnState.internalFilteredGarmentObjects = getArrayOfGarmentObjectsFromFilteringAllMemoIds(
                returnState.initialArrayOfPosts,
                returnState.memoizedIdArrays
            )
            // Update Timestamps
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            break;
        }
        case 'initialize filter state from fetch data or localStorage': {
            // Data has been fetched. Populate initial filter state
            // Generates garmentObjects from a combination of teetypes, teemeasures, fit and reliability.
            const initialArrayOfPosts = getInitialArrayOfPosts(action.teeMeasuresAveragesRows, action.newTeeTypesRows, action.userFitScoreWeightings, action.idealGarmentMeasurements);
            // Simple map to get array of ids
            const initialArrayOfIds = getInitialArrayOfIds(action.teeMeasuresAveragesRows);
            // Update Initial arrays of garment objects
            returnState.initialArrayOfPosts = initialArrayOfPosts;
            // Filter Checkbox objects:
            // This creates new filterCheckboxObjects from the full array of newteetypes and then spreads filterObjects from localStorage/prevState in case we had any saved in localStorage from previous filter adjustments by the user.
            returnState.filterCheckboxObjects.availablecolors = { ...getFilterCheckBoxObject(action.newTeeTypesRows), ...returnState.filterCheckboxObjects.availablecolors }
            returnState.filterCheckboxObjects.description = { ...getFilterCheckBoxObjectStrings({ label: "description", array: action.newTeeTypesRows }), ...returnState.filterCheckboxObjects.description }
            returnState.filterCheckboxObjects.manufacturer = { ...getFilterCheckBoxObjectStrings({ label: "manufacturer", array: action.newTeeTypesRows }), ...returnState.filterCheckboxObjects.manufacturer }
            // Filter slider objects against idealMeasures
            // Updates each filter slider object with users IdealMeasures. Makes each slider object's min and max within 20% of the idealmeasure
            const filterSliderObjectsUpdatedAgainstIdealMeasures = updateFilterSliderObjects(
                returnState.filterSliderObjects,
                action.idealGarmentMeasurements
            );
            // Check if any slider objects (existing in state, possibly from localStorage) are different from the default (ie, they've been adjusted by the user). If so we'll want to add them to the filterState
            const objectOfUpdateRecords = (Object.keys(returnState.filterSliderObjects) as TeeMeasureLabel[]).reduce<Record<TeeMeasureLabel, FilterObjectSlider>>((acc, cur) => {
                const returnObj = { ...acc };
                // Check if current state is different from default state
                const { min: defaultMin, max: defaultMax } = teesPropertiesObject[cur];
                const { min, max, disabled } = returnState.filterSliderObjects[cur]
                if (min !== defaultMin || max !== defaultMax) {
                    returnObj[cur] = {
                        min,
                        max,
                        disabled
                    };
                }
                return returnObj;
            }, {} as Record<TeeMeasureLabel, FilterObjectSlider>);
            returnState.filterSliderObjects = {
                ...filterSliderObjectsUpdatedAgainstIdealMeasures,
                ...objectOfUpdateRecords
            }
            // Memoised array of Ids
            Object.keys(returnState.memoizedIdArrays).forEach(thisMemoLabel => {
                returnState.memoizedIdArrays[thisMemoLabel as keyof typeof returnState.memoizedIdArrays] = initialArrayOfIds;
            });
            // Accommodate for initial filter values
            // I'm only doing initial checks for reliability so far. I may need to set up a function to deal with all updates.
            returnState.memoizedIdArrays.reliability = returnState.initialArrayOfPosts
                .filter(thisPostObj => {
                    // Filter out reliability id's where there is a value and the value is below the filter value. 
                    const thisValue = thisPostObj.reliability;
                    return (thisValue === null) || (thisValue >= returnState.reliability.min / 100)
                })
                .map(x => x.teemeasuresaverages_id);
            // Update memoised items against filter settings
            returnState.memoizedIdArrays.reliability = getMemoizedIdArrayFromFitOrReliability(
                returnState.initialArrayOfPosts,
                returnState.reliability.min,
                'reliability',
            );
            returnState.memoizedIdArrays.fit = getMemoizedIdArrayFromFitOrReliability(
                returnState.initialArrayOfPosts,
                returnState.fit.min,
                'fit',
            )
            returnState.memoizedIdArrays.manufacturer = getMemoizedIdArrayFromManufacturerOrDescrip(
                returnState.initialArrayOfPosts,
                "manufacturer",
                returnState.filterCheckboxObjects.manufacturer
            );
            returnState.memoizedIdArrays.description = getMemoizedIdArrayFromManufacturerOrDescrip(
                returnState.initialArrayOfPosts,
                "description",
                returnState.filterCheckboxObjects.description
            );
            returnState.memoizedIdArrays.availablecolors = getMemoizedIdArrayFromColors(
                returnState.initialArrayOfPosts,
                returnState.filterCheckboxObjects.availablecolors
            )
            const updatedMeasureSliderMemoizedIdArrays = getMemoizedIdArrayFromSizeSliders(
                returnState.initialArrayOfPosts,
                returnState.filterSliderObjects
            )
            returnState.memoizedIdArrays = { ...returnState.memoizedIdArrays, ...updatedMeasureSliderMemoizedIdArrays }
            // Update internal and external arrays against memoised arrays
            const filteredGarmentObjectArray = getArrayOfGarmentObjectsFromFilteringAllMemoIds(
                returnState.initialArrayOfPosts,
                returnState.memoizedIdArrays
            )

            returnState.externalFilteredGarmentObjects = filteredGarmentObjectArray;
            returnState.internalFilteredGarmentObjects = filteredGarmentObjectArray;
            // Update Timestamps
            returnState.timestamps.externalFilteredGarmentObjects = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            returnState.timestamps.fetchedData = currentTimestamp;
            break;
        }
        case "update slider min and max": {
            // Filter slider objects - set up so that they are disabled and that the default min/max are 20% either side of the user's ideal measures. This is triggered on start up or when the ideal measures change.
            returnState.filterSliderObjects = updateFilterSliderObjects(returnState.filterSliderObjects, action.idealGarmentMeasurements)
            break;
        }
        case "click select all checkbox": {
            // Will need to update: timestamps, internalFilterObject, postIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            // Iterate through all unique labels and check/uncheck all boxes depending on whether 'select all' checkbox is checked.
            Object.keys(returnState.filterCheckboxObjects[action.filterLabel]).forEach(thisLabel => {
                returnState.filterCheckboxObjects[action.filterLabel][thisLabel].checked = action.checked
            })
            // Get filtered array of Id's from memoised other filters.
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);
            const arrayOfPostIdsFromOtherFilters = arrayOfPostObjectsFromOtherFilters.map(x => x.teemeasuresaverages_id);
            // Update internal postIdArrays and internalFilteredGarmentObjects
            if (action.checked === false) {
                // if all unchecked then no filter applied (it's a bit unintuitive but makes more sense with text input filter).
                returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.map(x => x.teemeasuresaverages_id);
                // internalFilteredGarmentObjects will be initialArrayOfPosts filtered by the other memoised postIdArrays.
                returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters
            } else if (action.checked === true) {
                // Update internal postIdArrays and internalFilteredGarmentObjects if all checkboxes are checked.
                if (action.filterLabel === "manufacturer" || action.filterLabel === "description") {
                    // There will be a username associated with all posts. So since we've selected all checkboxes, the postIdArrays will be all values
                    returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.map(x => x.teemeasuresaverages_id);
                    // internalFilteredGarmentObjects will not need filtering any further since we aren't filtereing any values.
                    returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters
                }
                // Update interal postIdArray if checkboxes all true. So most of the initial posts array will be included.
                // This is probably going to be a different function for styles than for users, so if() it.
                if (action.filterLabel === "availablecolors") {
                    returnState.memoizedIdArrays[action.filterLabel] = [];
                    returnState.internalFilteredGarmentObjects = [];
                    returnState.initialArrayOfPosts.forEach((thisPost) => {
                        // If any of the tags array of this PostObject include any of the checked labels (ie all of them), then make the array from that.
                        if (thisPost.availablecolors.length > 0) {
                            returnState.memoizedIdArrays[action.filterLabel].push(thisPost.teemeasuresaverages_id);
                            // Update internal filtered posts. If this post object contains style tag in the checkbox labels, then concat this object to internalfilterposts if its postId is included in the array of posts from other filters.
                            if (arrayOfPostIdsFromOtherFilters.includes(thisPost.teemeasuresaverages_id)) {
                                returnState.internalFilteredGarmentObjects.push(thisPost)
                            }
                        }
                    }) // forEach block
                }
            }
            break;
        }
        case "click single checkbox": {
            // Will need to update: timestamps, internalFilterObject, memoizedIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            Object.keys(returnState.filterCheckboxObjects[action.filterLabel])
            returnState.filterCheckboxObjects[action.filterLabel][action.checkboxLabel].checked = action.checked;
            // Update memoizedIdArrays. 
            // Filter garments where the garment object property is an array, eg "availablecolors: [...]"
            if (action.filterLabel === "availablecolors") {
                // Filter only posts that include a style tag that has .checked === true
                returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.filter(thisPostObj =>
                    thisPostObj.availablecolors.some(thisTagLabel =>
                        returnState.filterCheckboxObjects[action.filterLabel][thisTagLabel].checked
                    )
                ).map(x => x.teemeasuresaverages_id) // This would probably be better iterating once in a reduce function.

            }
            // Filter garments where the garment object property is a simple string, eg "manufacturer: Uniqlo"
            if (action.filterLabel === "manufacturer" || action.filterLabel === "description") {
                // Filter only posts where user has .checked === true
                returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.filter(thisPostObj => {
                    if (action.filterLabel === "manufacturer") return returnState.filterCheckboxObjects[action.filterLabel][thisPostObj.manufacturer].checked;
                    if (action.filterLabel === "description") return returnState.filterCheckboxObjects[action.filterLabel][thisPostObj.description].checked;
                    throw new Error("error trying to filter returnState.initialArrayOfPosts.filter(thisPostObj...")
                }
                ).map(x => x.teemeasuresaverages_id) // This would probably be better iterating once in a reduce function.
            }
            // Update internalFilteredGarmentObjects
            // Get filtered array from memoised postId arrays
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);
            if (action.filterLabel === "availablecolors") {
                returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters.filter(thisPostObj =>
                    // Filter only posts where the updated postIdArray is included
                    thisPostObj.availablecolors.some(thisColorString =>
                        returnState.filterCheckboxObjects[action.filterLabel][thisColorString].checked
                    )
                )
            }
            if (action.filterLabel === "manufacturer" || action.filterLabel === "description") {
                returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters.filter(thisPostObj => {
                    // Filter only posts where the updated postIdArray is included
                    if (action.filterLabel === "manufacturer") return returnState.filterCheckboxObjects[action.filterLabel][thisPostObj.manufacturer].checked;
                    if (action.filterLabel === "description") return returnState.filterCheckboxObjects[action.filterLabel][thisPostObj.description].checked;
                    throw new Error("error trying to filter returnState.initialArrayOfPosts.filter(thisPostObj...")
                })
            }
            break;
        }
        case 'update slider': {
            // Will need to update: timestamps, internalFilterObject, memoizedIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            returnState.filterSliderObjects[action.filterLabel] = { ...returnState.filterSliderObjects[action.filterLabel], min: action.min, max: action.max, disabled: false }
            // Update postIdArray
            returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts
                .filter(thisPostObj => {
                    // If thisValue is between the min and max slider filter, then include it in filtered array. If the value is null, then also include it. (there may be a better solution?)
                    const thisValue = thisPostObj[action.filterLabel];
                    return (thisValue === null) || (thisValue >= action.min && thisValue <= action.max)
                })
                .map(x => x.teemeasuresaverages_id);
            // Update internalFilteredGarmentObjects
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);
            returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters.filter(thisObjFromOtherFilters => returnState.memoizedIdArrays[action.filterLabel].includes(thisObjFromOtherFilters.teemeasuresaverages_id))
            break;
        }
        case 'disable slider': {
            // Will need to update: timestamps, internalFilterObject, memoizedIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            returnState.filterSliderObjects[action.filterLabel] = { ...returnState.filterSliderObjects[action.filterLabel], disabled: true }
            // Update postIdArray
            returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.map(x => x.teemeasuresaverages_id);
            // Update internalFilteredGarmentObjects
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);

            returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters;
            break;
        }
        case "update fit/reliability slider": {
            // Will need to update: timestamps, internalFilterObject, memoizedIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            returnState[action.filterLabel] = { ...returnState[action.filterLabel], min: action.min, disabled: false }
            // Update postIdArray
            returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts
                .filter(thisPostObj => {
                    // If thisValue is greater than the min slider filter value then include this garment object in filtered array. If the value is null, then also include it. (there may be a better solution?)
                    const thisValue = thisPostObj[action.filterLabel];
                    return (thisValue === null) || (thisValue >= action.min / 100)
                })
                .map(x => x.teemeasuresaverages_id);

            // Update internalFilteredGarmentObjects
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);
            returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters.filter(thisObjFromOtherFilters => returnState.memoizedIdArrays[action.filterLabel].includes(thisObjFromOtherFilters.teemeasuresaverages_id))
            break;
        }
        case 'disable fit or reliability slider': {
            // Will need to update: timestamps, internalFilterObject, memoizedIdArrays, internalFilteredGarmentObjects
            // Update timestamp for memo comparisons
            returnState.timestamps[action.filterLabel] = currentTimestamp;
            returnState.timestamps.internalFilteredGarmentObjects = currentTimestamp;
            // Update filterObjects to update inputs (checkboxes, sliders, etc)
            returnState[action.filterLabel] = { ...returnState[action.filterLabel], disabled: true }
            // Update postIdArray
            returnState.memoizedIdArrays[action.filterLabel] = returnState.initialArrayOfPosts.map(x => x.teemeasuresaverages_id);

            // Update internalFilteredGarmentObjects
            const arrayOfPostObjectsFromOtherFilters = filterByOtherMemoisedIds(returnState.initialArrayOfPosts, returnState.memoizedIdArrays, action.filterLabel);
            returnState.internalFilteredGarmentObjects = arrayOfPostObjectsFromOtherFilters.filter(thisObjFromOtherFilters => returnState.memoizedIdArrays[action.filterLabel].includes(thisObjFromOtherFilters.teemeasuresaverages_id))
            break;
        }
        case "update external": {
            returnState.timestamps.externalFilteredGarmentObjects = currentTimestamp;
            returnState.externalFilteredGarmentObjects = returnState.internalFilteredGarmentObjects;
            break;
        }
        default:
            logAdminExternal({
                status: "filterReducer returning default value",
                action,
                state,
            })
            return state

    }
    logAdminExternal({
        status: "filterReducer(), returning out",
        actionType: action.type,
        returnState, state
    })
    localStorage.setItem("filterSettings", JSON.stringify({ filterCheckboxObjects: returnState.filterCheckboxObjects, filterSliderObjects: returnState.filterSliderObjects, fit: returnState.fit, reliability: returnState.reliability, timestamps: returnState.timestamps }))
    return returnState
}